import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Card, Skel } from './parts';
import { int, Visitors } from './api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

/** Sessions by weekday and hour (UK time) as a heatmap; the hovered cell's figures show underneath. */
export const BusyTimesCard: React.FC<{ v: Visitors | null; loading: boolean }> = ({ v, loading }) => {
  const [pick, setPick] = useState<[number, number] | null>(null);

  if (!v) {
    return (
      <Card i={7} area="time" title="Busiest times">
        {loading ? <Skel className="min-h-[220px] flex-1" /> : <p className="oa-cap">Busiest times appear once PostHog is connected.</p>}
      </Card>
    );
  }

  // ClickHouse weekdays run 1 (Mon) to 7 (Sun).
  const grid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => ({ sessions: 0, pageviews: 0 })));
  for (const c of v.heat) grid[c.dow - 1][c.hour] = { sessions: c.sessions, pageviews: c.pageviews };
  const max = Math.max(1, ...v.heat.map((c) => c.sessions));
  const peak = v.heat.reduce<[number, number] | null>((best, c) => (!best || c.sessions > grid[best[0]][best[1]].sessions ? [c.dow - 1, c.hour] : best), null);
  const cur = pick ?? peak;
  const cell = cur ? grid[cur[0]][cur[1]] : null;
  const empty = v.heat.length === 0;

  return (
    <Card i={7} area="time" title="Busiest times" actions={peak ? <span className="oa-chip is-plain" title="The busiest hour in this period">Peak: {DAYS[peak[0]]} {hh(peak[1])}</span> : null}>
      <div className="oa-heat flex-1" role="img" aria-label="Sessions by day of week and hour" onMouseLeave={() => setPick(null)}>
        {grid.map((row, d) => (
          <React.Fragment key={d}>
            <span className="oa-heat-y">{DAYS[d]}</span>
            {row.map((c, h) => (
              <i
                key={h}
                className={cn(cur && cur[0] === d && cur[1] === h && 'is-on', c.sessions === 0 && 'is-zero')}
                style={{ ['--a' as string]: `${Math.round((c.sessions / max) * 100)}%`, ['--i' as string]: d + h * 0.25 } as React.CSSProperties}
                onMouseEnter={() => setPick([d, h])}
              />
            ))}
          </React.Fragment>
        ))}
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="oa-heat-x">
            {h % 6 === 0 ? hh(h).slice(0, 2) : ''}
          </span>
        ))}
      </div>
      <p className="min-h-[1.5em] text-[12.5px] text-[var(--oa-muted)]">
        {empty ? (
          'No visits in this period.'
        ) : cur && cell ? (
          <>
            <b className="text-[var(--oa-text)]">
              {FULL[cur[0]]} {hh(cur[1])}–{hh((cur[1] + 1) % 24)}
            </b>{' '}
            · {int(cell.sessions)} sessions · {int(cell.pageviews)} page views
          </>
        ) : null}
      </p>
    </Card>
  );
};
